import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ModalProvider } from "react-easy-modals";
import { Provider } from "react-redux";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import useOutlineEnhancer from "@/modules/hooks/use-outline-enhancer";
import * as themes from "../assets/themes";
import ErrorBoundary from "../utils/error-boundary";
import polyglot from "../utils/polyglot";
import useToast from "./hooks/use-toast";
import useWindowSize from "./hooks/use-window-size";
import AskServiceProvider from "./providers/ask-service-provider";
import { ToastProvider } from "./providers/index";
import LoginProvider from "./providers/login-provider";
import { createAppStore } from "./store";

const GlobalStyle = createGlobalStyle`
  *[type="button"]{
    -webkit-appearance: none!important;
    -moz-appearance: none!important;
    appearance: none!important;
  }
  body.mousedown {
    button:focus, button:active {
      outline-width: 0!important;
      outline-color: transparent!important;
    }
  }
  body:not(.mousedown){
    button:active,
    button:focus,
    textarea:active,
    textarea:focus,
    select:active,
    select:focus,
    fieldset:active,
    fieldset:focus,
    a:active,
    a:focus,
    details:active,
    details:focus,
    summary:active,
    summary:focus,
    [tabindex]:active,
    [tabindex]:focus,
    [contenteditable="true"]:active,
    [contenteditable="true"]:focus,
    a:not(input[type="text"]):not(input[type="search"]):focus,
    input:not(input[type="text"]):not(input[type="search"]):active + label,
    input:not(input[type="text"]):not(input[type="search"]):focus + label {
      outline: solid ${themes.borderWidth.m} ${themes.colors.primary};
      outline-offset: ${themes.borderWidth.m};
    }
  }
`;

const ConditionalWrapper = ({ enabled, wrapper, children }) =>
  enabled ? wrapper(children) : children;

function BaseApp({ children, exclude = [], ...rest }) {
  return (
    <ToastProvider initial={{ success: rest.notice, danger: rest.alert }}>
      <Providers exclude={exclude}>{children}</Providers>
    </ToastProvider>
  );
}

const Providers = ({ exclude = [], children }) => {
  const toast = useToast();
  const [queryClient] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 10 * 1000, // 10 secondes cache stale
          gcTime: 10 * 60 * 1000, // 10min cache gc
          refetchOnWindowFocus: false,
          retry: (failureCount) => {
            return failureCount < 3;
          },
        },
        mutations: {
          onSuccess: (data: any) => {
            if (data?.data?.success_message) {
              toast.success(data.data.success_message);
            }
          },
          onError: (error) => {
            if (error?.response) {
              //is API error
              toast.danger(
                error.response?.data?.errorMessage ||
                  error.response?.data?.error_message ||
                  polyglot.t("common.problem_has_occurred")
              );
            } else {
              //is JS error
              toast.danger(polyglot.t("common.problem_has_occurred"));
            }
          },
        },
      },
    })
  );
  useOutlineEnhancer();

  // Structurer correctement l'état initial pour Redux
  const initialState = {
    rails: window.appStore || {},
  };

  const { width } = useWindowSize();

  useEffect(() => {
    width;
    const nav = document.querySelector("header");
    if (nav) {
      document.documentElement.style.setProperty("--nav-height", `${nav.clientHeight}px`);
    }
  }, [width]);

  const appStore = createAppStore(initialState);

  return (
    <ErrorBoundary>
      <Provider store={appStore}>
        <ConditionalWrapper
          enabled={!exclude.includes("QueryClient")}
          wrapper={(child) => (
            <QueryClientProvider client={queryClient}>{child}</QueryClientProvider>
          )}
        >
          <ModalProvider>
            <ConditionalWrapper
              enabled={!exclude.includes("ThemeProvider")}
              wrapper={(c) => <ThemeProvider theme={themes}>{c}</ThemeProvider>}
            >
              <GlobalStyle />
              <ConditionalWrapper
                enabled={!exclude.includes("AskService")}
                wrapper={(c) => <AskServiceProvider>{c}</AskServiceProvider>}
              >
                <ConditionalWrapper
                  enabled={!exclude.includes("Login")}
                  wrapper={(c) => <LoginProvider>{c}</LoginProvider>}
                >
                  {children}
                </ConditionalWrapper>
              </ConditionalWrapper>
            </ConditionalWrapper>
          </ModalProvider>
        </ConditionalWrapper>
      </Provider>
    </ErrorBoundary>
  );
};

export default BaseApp;
